import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import {
  getClusterAutoTuneHistory,
  getClusterAutoTuneRecommendations,
  getClusterAutoTuneSavingsTrends,
  getClusterAutoTuneStatus,
  toggleClusterAutoTune,
} from "@/helpers/apis";
import type { ClusterAutoTuneToggleResponse, ClusterRecType } from "./types";

export const CLUSTER_AT_STATUS_KEY = "cluster-auto-tune-status";
export const CLUSTER_AT_RECOMMENDATIONS_KEY = "cluster-auto-tune-recommendations";
export const CLUSTER_AT_HISTORY_KEY = "cluster-auto-tune-history";
export const CLUSTER_AT_SAVINGS_TRENDS_KEY = "cluster-auto-tune-savings-trends";

const hasInstance = (instanceId: number | string | undefined) =>
  instanceId !== undefined && instanceId !== "";

// Page-load status for the Auto Tune tab: master toggle position, a live
// preflight snapshot (for the disabled/blocked toggle state), and the
// recommendation / enabled counts.
export const useClusterAutoTuneStatus = (
  clusterId: string,
  instanceId: number | string | undefined,
  workspaceId?: string,
  enabled = true
) =>
  useQuery({
    queryKey: [CLUSTER_AT_STATUS_KEY, clusterId, instanceId, workspaceId] as const,
    queryFn: () =>
      getClusterAutoTuneStatus(clusterId, {
        instance_id: instanceId as number | string,
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      }),
    enabled: enabled && Boolean(clusterId) && hasInstance(instanceId),
    retry: false,
  });

// The cluster's active Worker Sizing (resize) + Auto Termination
// (auto_terminate) recommendations, each annotated with its resolved
// sub-toggle ``enabled`` state.
export const useClusterAutoTuneRecommendations = (
  clusterId: string,
  instanceId: number | string | undefined,
  workspaceId?: string,
  enabled = true
) =>
  useQuery({
    queryKey: [CLUSTER_AT_RECOMMENDATIONS_KEY, clusterId, instanceId, workspaceId] as const,
    queryFn: () =>
      getClusterAutoTuneRecommendations(clusterId, {
        instance_id: instanceId as number | string,
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      }),
    enabled: enabled && Boolean(clusterId) && hasInstance(instanceId),
    retry: false,
  });

interface ClusterHistoryParams {
  instance_id?: number | string;
  workspace_id?: string;
  event_types?: string;
  period?: number;
  page?: number;
  page_size?: number;
}

export const useClusterAutoTuneHistory = (
  clusterId: string,
  params: ClusterHistoryParams,
  enabled = true
) =>
  useQuery({
    queryKey: [CLUSTER_AT_HISTORY_KEY, clusterId, params] as const,
    queryFn: () =>
      getClusterAutoTuneHistory(clusterId, {
        instance_id: params.instance_id as number | string,
        ...(params.workspace_id ? { workspace_id: params.workspace_id } : {}),
        ...(params.event_types ? { event_types: params.event_types } : {}),
        ...(params.period !== undefined ? { period: params.period } : {}),
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.page_size !== undefined ? { page_size: params.page_size } : {}),
      }),
    enabled: enabled && Boolean(clusterId) && hasInstance(params.instance_id),
    retry: false,
  });

// Per-day Possible Savings series for the cluster. ``actual`` is always
// empty (no realized-savings mart); ``potential`` is daily spend × the
// combined active-rec savings fraction, computed server-side.
export const useClusterAutoTuneSavingsTrends = (
  clusterId: string,
  instanceId: number | string | undefined,
  startDate: string,
  endDate: string,
  enabled = true
) =>
  useQuery({
    queryKey: [CLUSTER_AT_SAVINGS_TRENDS_KEY, clusterId, instanceId, startDate, endDate] as const,
    queryFn: () =>
      getClusterAutoTuneSavingsTrends(clusterId, {
        instance_id: instanceId as number | string,
        start_date: startDate,
        end_date: endDate,
        granularity: "day",
      }),
    enabled: enabled && Boolean(clusterId) && hasInstance(instanceId) && Boolean(startDate),
    retry: false,
  });

// Enable/disable Auto Tune for the cluster. ``recType`` null is the master
// toggle (flips both sub-toggles). Invalidates status / recommendations /
// history on success so the UI re-renders from server truth — the toggle
// records config + audit only, nothing is applied to Databricks.
export const useToggleClusterAutoTune = ({
  clusterId,
  instanceId,
  workspaceId,
}: {
  clusterId: string;
  instanceId: number | string;
  workspaceId?: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation<
    ClusterAutoTuneToggleResponse | undefined,
    Error,
    { enabled: boolean; recType?: ClusterRecType | null }
  >({
    mutationKey: ["toggle-cluster-auto-tune", clusterId, instanceId, workspaceId] as const,
    mutationFn: ({ enabled, recType }) =>
      toggleClusterAutoTune(
        clusterId,
        { instance_id: instanceId, workspace_id: workspaceId },
        { enabled, rec_type: recType ?? null }
      ),
    onSuccess: async result => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CLUSTER_AT_STATUS_KEY, clusterId] }),
        queryClient.invalidateQueries({
          queryKey: [CLUSTER_AT_RECOMMENDATIONS_KEY, clusterId],
        }),
        queryClient.invalidateQueries({ queryKey: [CLUSTER_AT_HISTORY_KEY, clusterId] }),
      ]);
      // The toggle endpoint returns 200 with ``preflight.blocked`` when the
      // enable was refused (e.g. SP lacks CAN MANAGE) — surface that as an
      // error rather than a false "saved".
      if (result?.preflight?.blocked) {
        toast({
          variant: "destructive",
          title: "Auto Tune unavailable",
          description:
            result.preflight.actionable_message ?? "This cluster can't be managed right now.",
        });
        return;
      }
      toast({ title: "Auto Tune updated" });
    },
    onError: (err: Error) => {
      toast({
        variant: "destructive",
        title: "Toggle failed",
        description: err instanceof Error ? err.message : "Could not change Auto Tune state.",
      });
    },
  });
};
